import { makeStyles } from '@material-ui/styles'
import Button from 'core/elements/button'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import React, { useCallback, useState } from 'react'
import { convertToTitleCase } from '../infrastructure/blueprint/helpers'
import { ACTION_LIMITATION_TOOLTIPS } from './tooltipConstants'

const useStyles = makeStyles<Theme>((theme) => ({
  nestedModal: {
    zIndex: 20000,
    '& .modal-body .progress-root': {
      height: 'auto',
      minHeight: 'auto',
    },
  },
  button: {
    width: 100,
  },
}))

const enum VolumeStatus {
  Reserved = 'reserved',
  Detaching = 'detaching',
  Attaching = 'attaching',
  Available = 'available',
  InUse = 'in-use',
}

const InProgressStates = [VolumeStatus.Detaching, VolumeStatus.Attaching, VolumeStatus.Reserved]

interface VolumeActionButtonProps {
  volume: any
  vm: any
  detachVolumeFromVM: (volume: any) => Promise<void>
  attachVolumeToVm: (volume: any) => Promise<void>
  localLoading: Record<string, boolean>
  setLocalLoading: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
}

const VolumeActionButton = ({
  volume,
  vm,
  detachVolumeFromVM,
  attachVolumeToVm,
  localLoading,
  setLocalLoading,
}: VolumeActionButtonProps) => {
  const classes = useStyles()
  const [modalOpen, setModalOpen] = useState(false)
  // Check if this volume is attached to the current VM
  const isVolumeAttachedToThisVm = volume?.attachedVms?.some(
    (attachedVm: any) => attachedVm?.id === vm?.id,
  )

  // Check if this is a boot volume (cannot be detached from running VM)
  const isBootVolume =
    volume?.bootable &&
    vm?.volumeDetails &&
    Object.values(vm.volumeDetails).some(
      (volDetail: any) => volDetail?.id === volume.id && volDetail?.boot_index === 0,
    )

  // Check if VM is running (boot volumes cannot be detached from running VMs)
  const isVmRunning = vm?.state === 'active'

  // ISO-based volumes require VM to be stopped for both attach and detach
  const isIsoVolume = volume?.volume_image_metadata?.disk_format === 'iso'
  const isVmStopped = vm?.state === 'stopped'
  const isIsoVolumeVmNotStopped = isIsoVolume && !isVmStopped

  // Determine if detach should be disabled
  const isDetachDisabled =
    (isVolumeAttachedToThisVm && isBootVolume && isVmRunning) || isIsoVolumeVmNotStopped

  const isAttachDisabled = isIsoVolumeVmNotStopped

  const actionInProgress = InProgressStates.includes(volume.status) || localLoading[volume.id]

  const onButtonSubmit = useCallback(() => {
    setLocalLoading((prev) => ({ ...prev, [volume.id]: true }))
    if (isVolumeAttachedToThisVm) {
      detachVolumeFromVM(volume)
    } else {
      attachVolumeToVm(volume)
    }
    setModalOpen(false)
  }, [isVolumeAttachedToThisVm, detachVolumeFromVM, attachVolumeToVm, volume])

  return (
    <>
      <ModalForm
        title={isVolumeAttachedToThisVm ? 'Detach Volume' : 'Attach Volume'}
        onSubmit={onButtonSubmit}
        onClose={() => setModalOpen(false)}
        submitTitle={isVolumeAttachedToThisVm ? 'Detach' : 'Attach'}
        panel="dialog"
        pageClassName={classes.nestedModal}
        open={modalOpen}
      >
        {isVolumeAttachedToThisVm ? (
          <Text variant="body2">
            Are you sure you want to detach this volume <b>{volume.name || volume.id}</b>? Detaching
            will make the volume unavailable to the virtual machine and may disrupt any running
            processes dependent on it.
          </Text>
        ) : (
          <Text variant="body2">
            Are you sure you want to attach this volume <b>{volume.name || volume.id}</b> to the
            selected virtual machine?
          </Text>
        )}
      </ModalForm>
      <Button
        id="attachDetachVolume"
        size="small"
        className={classes.button}
        disabled={
          actionInProgress || (isVolumeAttachedToThisVm ? isDetachDisabled : isAttachDisabled)
        }
        loading={actionInProgress}
        onClick={() => {
          setModalOpen(true)
        }}
        variant={isVolumeAttachedToThisVm ? 'tertiary' : 'primary'}
        icon={isVolumeAttachedToThisVm ? 'trash-alt' : 'plus'}
        info={
          actionInProgress
            ? convertToTitleCase(volume?.status)
            : isIsoVolumeVmNotStopped
            ? ACTION_LIMITATION_TOOLTIPS.ISO_VOLUME_VM_NOT_STOPPED
            : ''
        }
      >
        {isVolumeAttachedToThisVm ? 'Detach' : 'Attach'}
      </Button>
    </>
  )
}

export default VolumeActionButton
